<template>
  <section class="fg-page">
    <!-- Header pleine largeur — même grammaire que l'étape Traversée. -->
    <header class="fg-page__header">
      <BackButton @click="router.goTo(BookingStep.Results)" />
      <div class="fg-page__titles">
        <h1 class="fg-page__title">{{ t('options.title') }}</h1>
        <p class="fg-page__subtitle">{{ t('options.subtitle') }}</p>
      </div>
      <Stepper current="options" />
    </header>

    <div class="fg-page-with-summary">
    <div class="fg-page-with-summary__main fg-options">
      <p v-if="!outwardOptions.length && !returnOptions.length" class="fg-empty">
        {{ t('options.none') }}
      </p>

      <template v-else>
        <!-- Bloc Aller -->
        <article v-if="outwardOptions.length" class="fg-options-group">
          <header class="fg-options-group__head">
            <span class="fg-options-group__icon"><Icon name="ship" :size="16" /></span>
            <div class="fg-options-group__title-wrap">
              <span class="fg-options-group__eyebrow">{{ t('options.outwardGroup') }}</span>
              <p v-if="outwardRoute" class="fg-options-group__route">
                {{ outwardRoute.from }} <span class="fg-options-group__arrow">→</span> {{ outwardRoute.to }}
              </p>
            </div>
          </header>
          <ul class="fg-options-group__list">
            <li
              v-for="opt in outwardOptions"
              :key="`out-${opt.key}`"
              class="fg-option-card"
              :class="{ 'is-selected': opt.quantity > 0 }"
            >
              <div class="fg-option-card__icon">
                <Icon :name="optionIcon(opt)" :size="18" />
              </div>
              <div class="fg-option-card__main">
                <span class="fg-option-card__name">
                  {{ humanizeLabel(opt.name || opt.code) }}
                  <span v-if="opt.kind === 'service'" class="fg-option-card__kind">{{ t('options.service') }}</span>
                </span>
                <span v-if="opt.description && !isPlaceholderDesc(opt.description)" class="fg-option-card__desc">
                  {{ opt.description }}
                </span>
              </div>
              <div class="fg-option-card__right">
                <span class="fg-option-card__price">
                  {{ formatPrice(opt.amount, opt.currency, locale) }}
                </span>
                <div class="fg-stepper">
                  <button
                    type="button"
                    class="fg-stepper__btn"
                    :aria-label="t('common.minus') || '−'"
                    :disabled="opt.quantity === 0"
                    @click="decrement('outward', opt)"
                  >−</button>
                  <span class="fg-stepper__value">{{ opt.quantity }}</span>
                  <button
                    type="button"
                    class="fg-stepper__btn"
                    :aria-label="t('common.plus') || '+'"
                    :disabled="opt.quantity >= maxQty"
                    @click="increment('outward', opt)"
                  >+</button>
                </div>
              </div>
            </li>
          </ul>
        </article>

        <!-- Bloc Retour -->
        <article v-if="returnOptions.length" class="fg-options-group">
          <header class="fg-options-group__head">
            <span class="fg-options-group__icon"><Icon name="ship" :size="16" /></span>
            <div class="fg-options-group__title-wrap">
              <span class="fg-options-group__eyebrow">{{ t('options.returnGroup') }}</span>
              <p v-if="returnRoute" class="fg-options-group__route">
                {{ returnRoute.from }} <span class="fg-options-group__arrow">→</span> {{ returnRoute.to }}
              </p>
            </div>
          </header>
          <ul class="fg-options-group__list">
            <li
              v-for="opt in returnOptions"
              :key="`ret-${opt.key}`"
              class="fg-option-card"
              :class="{ 'is-selected': opt.quantity > 0 }"
            >
              <div class="fg-option-card__icon">
                <Icon :name="optionIcon(opt)" :size="18" />
              </div>
              <div class="fg-option-card__main">
                <span class="fg-option-card__name">
                  {{ humanizeLabel(opt.name || opt.code) }}
                  <span v-if="opt.kind === 'service'" class="fg-option-card__kind">{{ t('options.service') }}</span>
                </span>
                <span v-if="opt.description && !isPlaceholderDesc(opt.description)" class="fg-option-card__desc">
                  {{ opt.description }}
                </span>
              </div>
              <div class="fg-option-card__right">
                <span class="fg-option-card__price">
                  {{ formatPrice(opt.amount, opt.currency, locale) }}
                </span>
                <div class="fg-stepper">
                  <button
                    type="button"
                    class="fg-stepper__btn"
                    :aria-label="t('common.minus') || '−'"
                    :disabled="opt.quantity === 0"
                    @click="decrement('return', opt)"
                  >−</button>
                  <span class="fg-stepper__value">{{ opt.quantity }}</span>
                  <button
                    type="button"
                    class="fg-stepper__btn"
                    :aria-label="t('common.plus') || '+'"
                    :disabled="opt.quantity >= maxQty"
                    @click="increment('return', opt)"
                  >+</button>
                </div>
              </div>
            </li>
          </ul>
        </article>
      </template>

      <footer class="fg-actionbar">
        <div class="fg-actionbar__total">
          <span class="fg-actionbar__total-label">{{ t('results.total') }}</span>
          <span class="fg-actionbar__total-value">{{ totalFormatted }}</span>
          <span class="fg-actionbar__total-note">{{ t('summary.taxesIncluded') }}</span>
        </div>
        <button class="fg-btn fg-btn--primary" @click="router.goTo(BookingStep.Booking)">
          {{ t('options.continue') }}
        </button>
      </footer>
    </div>
    <BookingSummary class="fg-page-with-summary__aside" />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useStore } from '../store'
import { useI18n } from '../i18n'
import { useRouter } from '../router'
import { useTracker } from '../tracker'
import { routeKey } from '../utils/track-meta'
import { BookingStep } from '../../../shared/types/enums'
import type { Tariff } from '../../../shared/types'
import { computeBookingTotal, setUnifiedMealQuantity, type UnifiedMeal } from '../../../shared/rules'
import { mealDir, unifiedMeals, mealsTotal } from '../utils/meals'
import { formatPort, formatPrice, humanizeLabel } from '../utils/format'
import BackButton from './atoms/BackButton.vue'
import BookingSummary from './atoms/BookingSummary.vue'
import Stepper from './atoms/Stepper.vue'
import Icon from './Icon.vue'

const store = useStore()
const router = useRouter()
const { t, locale } = useI18n()
const tracker = useTracker()

// Jalon funnel : l'écran options/repas est affiché.
onMounted(() => {
  tracker.emitOnce('options_viewed', { route: routeKey(store.search.journeys) })
})

// Repas proposés par direction — liste unifiée des règles SHARED (identiques au
// SaaS, cf. MealOptions.vue), lue depuis le store et couvrant tous les formats
// providers. La quantité choisie est PERSISTÉE sur l'objet du store (cf.
// increment/decrement) : elle survit à la navigation, alimente le total ET le
// payload de création de ticket.
const outwardOptions = computed<UnifiedMeal[]>(() => unifiedMeals(store, 'outward'))
const returnOptions = computed<UnifiedMeal[]>(() => unifiedMeals(store, 'return'))

// Plafond par repas = nombre de voyageurs du dossier (au moins 1).
const maxQty = computed(() => Math.max(1, store.search.passengers.length))

// Total courant = tarifs sélectionnés (tous groupes × aller/retour) + repas
// choisis. Sommer le seul groupe 1 sous-comptait un dossier > 4 pax.
const totalFormatted = computed(() => {
  const tariffs = [
    store.selectedTariffs.outward.group1, store.selectedTariffs.outward.group2,
    store.selectedTariffs.return.group1, store.selectedTariffs.return.group2,
  ].filter((x): x is Tariff => x !== null && x !== undefined)
  const total = computeBookingTotal({ tariffs })
  return formatPrice(total.amount + mealsTotal(store), total.currency, locale.value)
})

const outwardRoute = computed<{ from: string; to: string } | null>(() => {
  const first = (store.availabilities.outward.all ?? [])[0]
  if (!first) return null
  return { from: formatPort(first.depart_name), to: formatPort(first.arrive_name) }
})
const returnRoute = computed<{ from: string; to: string } | null>(() => {
  const first = (store.availabilities.return.all ?? [])[0]
  if (!first) return null
  return { from: formatPort(first.depart_name), to: formatPort(first.arrive_name) }
})

// Persistance directe sur le store via la règle shared `setUnifiedMealQuantity`
// (retrouve l'objet meal/option réel dans la direction et mute sa quantité).
// Repas : tasse ; service : wifi / étoile (VIP et autres).
function optionIcon(opt: UnifiedMeal): 'meal' | 'wifi' | 'star' {
  if (opt.kind !== 'service') return 'meal'
  const text = `${opt.code ?? ''} ${opt.name ?? ''}`.toUpperCase()
  return text.includes('WI-FI') || text.includes('WIFI') || /\bW\dGB\b|\bWF/.test(text) ? 'wifi' : 'star'
}

function increment(direction: 'outward' | 'return', opt: UnifiedMeal): void {
  if (opt.quantity >= maxQty.value) return
  setUnifiedMealQuantity(mealDir(store, direction), opt, opt.quantity + 1)
}

function decrement(direction: 'outward' | 'return', opt: UnifiedMeal): void {
  setUnifiedMealQuantity(mealDir(store, direction), opt, opt.quantity - 1)
}

// Descriptions placeholders venant du provider GNV (ex: "description::60a76...")
// — on les masque côté UI au lieu d'afficher un code technique à l'utilisateur.
function isPlaceholderDesc(desc: string): boolean {
  return /^description::[0-9a-f]+$/i.test(desc.trim())
}
</script>
